import React, { createContext, useContext, useEffect, useState } from "react";
import type { UserRole } from "../hooks/useFetchUserRole";

interface AuthContextProps {
    signOut: () => void;
    setShowStepUpAuth: React.Dispatch<React.SetStateAction<boolean>>;
    tokensParsed: Record<string, any>;
    toggleShowAuthenticatorManager: () => void;
    idToken: any;
    signInStatus: string;
    userRole: UserRole | null;
}
 
const AuthContext = createContext<AuthContextProps | null>(null);

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
    
};

interface AuthProviderProps {
    children: React.ReactNode;
    signOut: () => void;
    setShowStepUpAuth: React.Dispatch<React.SetStateAction<boolean>>;
    toggleShowAuthenticatorManager: () => void;
    tokensParsed: any;
    idToken: any;
    signInStatus: string;
}

export const AuthProvider = ({
    children,
    signOut,
    setShowStepUpAuth,
    tokensParsed,
    toggleShowAuthenticatorManager,
    idToken,
    signInStatus
}: AuthProviderProps) => {
    const [userRole, setUserRole] = useState<UserRole | null>(null);

    useEffect(() => {
        if (signInStatus !== "SIGNED_IN" || !idToken || !tokensParsed?.idToken?.email) {
            setUserRole(null);
            return;
        }

        const fetchUserRole = async () => {
            try {
                const response = await fetch(
                    `${import.meta.env.VITE_AWS_LAMBDA_API}get-user`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${idToken}`,
                        },
                    }
                );

                if (!response.ok) {
                    // If endpoint doesn't exist yet, fail silently
                    if (response.status === 404 || response.status === 501) {
                        console.log("User role endpoint not available yet");
                        return;
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                console.log("User role API response:", data);
                const userData = typeof data === "object" && data.data ? data.data : data;
                console.log("Parsed userData:", userData);
                
                if (userData.role) {
                    console.log("Setting user role to:", userData.role);
                    setUserRole(userData.role);
                } else {
                    console.warn("No role found in userData:", userData);
                }
            } catch (error) {
                // Fail silently if endpoint doesn't exist
                console.log("Error fetching user role:", error);
            }
        };

        fetchUserRole();
    }, [signInStatus, idToken, tokensParsed?.idToken?.email]);

    return (
        <AuthContext.Provider value={{
            signOut,
            setShowStepUpAuth,
            tokensParsed,
            idToken,
            toggleShowAuthenticatorManager,
            signInStatus,
            userRole
        }}>
            {children}
        </AuthContext.Provider>
    );
}

